/**
 * Per-candidate-row conversion-rate display orientation. Pure view state — not
 * mapping data — so it lives apart from {@link ImportRowDraft} and survives
 * materialization (which clears the draft). The stored `quantityFactor` is
 * always canonical (target-unit per source-unit); when a row is swapped the
 * rate field shows and reads back its inverse. Keyed by candidate-row id to
 * avoid cross-row leakage and to persist across reloads.
 */

import { useSyncExternalStore } from 'react';

const STORAGE_VERSION = 1;

const storageKey = (rowId: string): string =>
  `nodon.import-rate-swapped.v${STORAGE_VERSION}.${rowId}`;

const read = (rowId: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(storageKey(rowId)) === '1';
  } catch {
    return false;
  }
};

/** Persist (or clear) the swap orientation for a row. */
export const setRateSwapped = (rowId: string, swapped: boolean): void => {
  if (typeof window !== 'undefined') {
    try {
      if (swapped) window.localStorage.setItem(storageKey(rowId), '1');
      else window.localStorage.removeItem(storageKey(rowId));
    } catch {
      // Quota / disabled storage — drop silently.
    }
  }
  snapshotCache.set(rowId, swapped);
  notify(rowId);
};

// ---------------------------------------------------------------------------
// In-tab subscriptions (same-tab readers need their own channel; `storage`
// events only fire across tabs). Mirrors import-row-draft.ls.
// ---------------------------------------------------------------------------

type Listener = () => void;
const listeners = new Map<string, Set<Listener>>();

const notify = (rowId: string): void => {
  listeners.get(rowId)?.forEach((l) => l());
};

const subscribe = (rowId: string, listener: Listener): (() => void) => {
  let set = listeners.get(rowId);
  if (!set) {
    set = new Set();
    listeners.set(rowId, set);
  }
  set.add(listener);
  return () => {
    set?.delete(listener);
    if (set && set.size === 0) listeners.delete(rowId);
  };
};

const snapshotCache = new Map<string, boolean>();
const getSnapshot = (rowId: string): boolean => {
  let cached = snapshotCache.get(rowId);
  if (cached === undefined) {
    cached = read(rowId);
    snapshotCache.set(rowId, cached);
  }
  return cached;
};

/** React hook: subscribe to a row's swap orientation. */
export const useRateSwapped = (rowId: string): boolean =>
  useSyncExternalStore(
    (l) => subscribe(rowId, l),
    () => getSnapshot(rowId),
    () => false,
  );
